import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableRow from '@mui/material/TableRow';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { capitalize } from 'lodash-es';

import { Order } from '~hooks/useOrders/models';
import formatNumber from '~utils/formatNumber';

const minutesToHoursAndMinutesString = (value: number) => {
  const minutesToHours = Math.floor(value / 60);
  const minutesLeft = Math.floor(value % 60);

  const minutesToHoursAsString = minutesToHours > 0 ? `${minutesToHours}hr ` : '';
  const minutesLeftAsString = `${minutesLeft}min`;

  return `${minutesToHoursAsString}${minutesLeftAsString}`;
};

export const makeCycleAvgCellString = (order: Order) => {
  return minutesToHoursAndMinutesString(order.loadCycleAvg);
};

const makeLoadsPickupAvgCellString = (order: Order) => {
  return minutesToHoursAndMinutesString(order.loadsPickupTimeMinutesAvg);
};

const OrderSummaryRow = ({ order }: { order: Order }) => {
  const theme = useTheme();
  const { material, orderSummary } = order;
  const deliveredQuantity =
    orderSummary?.deliveredQuantities.find(
      (deliveredQuantity) => deliveredQuantity.materialName === material?.name,
    ) || orderSummary?.deliveredQuantities[0];

  return (
    <Box
      sx={{
        borderRadius: theme.brandV2.borderRadius,
        border: `1px solid ${theme.brandV2.colors.treadGray7}`,
        overflow: 'hidden',
      }}
    >
      <Table
        sx={{
          backgroundColor: theme.brandV2.colors.treadGray8,
        }}
      >
        <TableBody>
          <TableRow>
            <OrderSummaryAttribute
              label={t('live_map.active_trucks')}
              text={`${orderSummary?.acceptedJobsCount ?? 0} / ${orderSummary?.jobsCount ?? 0}`}
            />
            <OrderSummaryAttribute
              label={capitalize(`${t('common.delivered')}`)}
              text={`${formatNumber(deliveredQuantity?.delivered ?? 0)} / ${formatNumber(deliveredQuantity?.total ?? 0)}`}
            />
            <OrderSummaryAttribute
              label={t('live_map.pickup_avg')}
              text={makeLoadsPickupAvgCellString(order)}
            />
            <OrderSummaryAttribute
              label={t('live_map.cycle_avg')}
              text={makeCycleAvgCellString(order)}
            />
          </TableRow>
        </TableBody>
      </Table>
    </Box>
  );
};

interface OrderAttributeProps {
  label: string;
  text: string;
}

const OrderSummaryAttribute = ({ label, text }: OrderAttributeProps) => {
  const theme = useTheme();

  return (
    <TableCell
      align="center"
      padding="none"
      sx={{
        paddingY: 1,
        borderRight: `1px solid ${theme.brandV2.colors.treadGray7}40`,
        borderBottom: 'none',
        '&:last-child': {
          borderRight: 'none',
        },
      }}
      width={'25%'}
    >
      <Typography
        sx={{
          fontSize: '12px',
          fontWeight: 600,
        }}
      >
        {text}
      </Typography>
      <Typography
        color={theme.brandV2.colors.treadGray4}
        sx={{
          fontSize: '12px',
          fontWeight: 600,
        }}
      >
        {label}
      </Typography>
    </TableCell>
  );
};

export default OrderSummaryRow;
